import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useLanguage } from '../contexts/LanguageContext';
import { SubscriptionCancelCards, AnnualSwitchOffer } from '../components/SubscriptionCancel';

const Refund = () => {
  const L = useLanguage();
  const [transactions, setTransactions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTxId, setSelectedTxId] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [refundReasonType, setRefundReasonType] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleRefundRequest = async (transactionid, userid, reason) => {
    try {
      const response = await fetch('https://api.viralgpt.com/refund/request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          transactionid,
          userid,
		  reason
        }),
      });

      if (response.ok) {
        alert(L.refundPage.submitSuccess);
      } else {
        alert(L.refundPage.submitError);
      }
    } catch (error) {
      alert(L.refundPage.submitError);
    }
  };

  const handleLookup = async (e) => {
    e.preventDefault();
    const email = e.target.email.value;
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('https://api.viralgpt.com/refund/lookup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setTransactions(data);
      } else {
        setError(data.error || L.refundPage.lookupFailed);
      }
    } catch (error) {
      setError(L.refundPage.requestError);
    } finally {
      setLoading(false);
    }
  };

  const openRefundModal = (transactionid, userid) => {
    setSelectedTxId(transactionid);
    setSelectedUserId(userid);
    setRefundReasonType('');
    setRefundReason('');
    setIsModalOpen(true);
  };

  const closeRefundModal = () => {
    setIsModalOpen(false);
    setSubmitting(false);
    setRefundReasonType('');
    setRefundReason('');
    setSelectedTxId(null);
    setSelectedUserId(null);
  };

  // The stored charge type is what tells a plan payment apart from an
  // auto-refill bill or a spin purchase — "subcharge" told the customer nothing.
  const typeLabel = (tx) => {
    const key = tx.charge_type || tx.transaction_type;
    return L.refundPage.txTypes[key] || key;
  };

  const submitRefund = async (e) => {
    e.preventDefault();
    const reason = refundReason.trim();
    if (!reason) {
      alert(L.refundPage.enterReason);
      return;
    }
    setSubmitting(true);
    try {
      await handleRefundRequest(selectedTxId, selectedUserId, reason);
    } finally {
      setSubmitting(false);
      closeRefundModal();
    }
  };

  return (
    <>
      <Navbar />
      <div className="container py-5">
        <div className="text-center mb-5">
          <h1 className="text-instagram"><strong>{L.refundPage.title}</strong></h1>
          <p className="mt-3">
            {L.refundPage.intro}
          </p>
        </div>

        <div className="row justify-content-center">
          <div className="col-md-8">
            <div className="card shadow-sm">
              <div className="card-body">
                <h5 className="card-title mb-4">{L.refundPage.lookupTitle}</h5>

                <form onSubmit={handleLookup}>
                  <div className="mb-3">
                    <input
                      type="email"
                      name="email"
                      className="form-control"
                      placeholder={L.refundPage.emailPlaceholder}
                      required
                    />
                  </div>
                  <button type="submit" className="btn bg-instagram text-white w-100" disabled={loading}>
                    {loading ? L.common.loading : L.refundPage.lookupOrders}
                  </button>
                </form>

                {error && (
                  <div className="alert alert-danger mt-3">{error}</div>
                )}

                {transactions && transactions.transactions && (
                  <div className="mt-4">
                    <h6>{L.refundPage.historyTitle}</h6>
                    {transactions.blocked_bin && (
                      <div className="alert alert-warning" style={{ fontSize: '0.88rem' }}>
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        {L.refundPage.blockedBinNotice}
                      </div>
                    )}
                    <div className="table-responsive">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>{L.refundPage.thDate}</th>
                            <th>{L.refundPage.thType}</th>
                            <th>{L.refundPage.thAmount}</th>
                            <th>{L.refundPage.thAction}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {transactions.transactions.map((tx) => (
                            <tr key={tx.orderid}>
                              <td>{new Date(tx.date).toLocaleDateString()}</td>
                              {/* Display label only — the raw `subcharge` value still
                                  drives the switch-to-monthly lookup below. */}
                              <td>{typeLabel(tx)}</td>
                              <td>${tx.price}</td>
                              <td>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => openRefundModal(tx.transactionid, transactions.user_id)}
                                  disabled={transactions.blocked_bin}
                                  title={transactions.blocked_bin ? L.refundPage.blockedBinTitle : ''}
                                >
                                  {L.refundPage.requestRefund}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <SubscriptionCancelCards data={transactions} setData={setTransactions} />

                <div className="mt-3 text-muted small">
                  <p className="mb-1">
                    <i className="fa-solid fa-info-circle me-1"></i>
                    {L.refundPage.reviewNote}
                  </p>
                  <p className="mb-1">
                    <i className="fa-solid fa-redo me-1"></i>
                    {L.refundPage.refillHint} <a href="/refill" className="link-blue">{L.refundPage.refillLink}</a>
                  </p>
                  <p className="mb-1">
                    <i className="fa-solid fa-ban me-1"></i>
                    {L.refundPage.cancelHint} <a href="/cancel" className="link-blue">{L.refundPage.cancelLink}</a>
                  </p>
                  <p className="mb-0">
                    {L.refundPage.immediateAssistance}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {isModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1050
          }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              closeRefundModal();
            }
          }}
        >
          <div
            style={{ 
              background: '#fff', 
              borderRadius: 8, 
              width: '100%', 
              maxWidth: 520,
              margin: '0 16px'
            }}
          >
            <form onSubmit={submitRefund}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #dee2e6' }}>
                <h5 className="modal-title mb-0">{L.refundPage.requestRefund}</h5>
              </div>
              <div style={{ padding: 20 }}>
                <div className="mb-3">
                  <label htmlFor="refund-reason-type" className="form-label">{L.refundPage.reasonLabel} <span className="text-danger">*</span></label>
                  <select
                    id="refund-reason-type"
                    className="form-select"
                    value={refundReasonType}
                    onChange={(e) => {
                      setRefundReasonType(e.target.value);
                      setRefundReason('');
                    }}
                    required
                  >
                    <option value="">{L.refundPage.selectReason}</option>
                    {transactions?.active_subscription?.planInterval === 'annual' && (
                      <option value="accidental_annual">{L.refundPage.optAccidentalAnnual}</option>
                    )}
                    <option value="missing_followers">{L.refundPage.optMissingFollowers}</option>
                    <option value="other">{L.refundPage.optOther}</option>
                  </select>
                </div>

                {refundReasonType === 'accidental_annual' && (
                  <AnnualSwitchOffer data={transactions} setData={setTransactions} onDone={closeRefundModal} />
                )}

                {refundReasonType === 'missing_followers' && (
                  <div className="alert alert-info">
                    <p className="mb-2">{L.refundPage.refillFree}</p>
                    <a
                      href="/dashboard?tab=refills"
                      className="btn btn-primary bg-instagram border-0 text-white"
                    >
                      <i className="fas fa-redo me-2"></i>
                      {L.refundPage.goToRefill}
                    </a>
                  </div>
                )}

                {refundReasonType === 'other' && (
                  <div className="mb-3">
                    <label htmlFor="refund-reason" className="form-label">{L.refundPage.detailsLabel} <span className="text-danger">*</span></label>
                    <textarea
                      id="refund-reason"
                      className="form-control"
                      placeholder={L.refundPage.detailsPlaceholder}
                      rows={4}
                      value={refundReason}
                      onChange={(e) => setRefundReason(e.target.value)}
                      required
                    />
                    <small className="text-muted">{L.refundPage.detailsHelp}</small>
                  </div>
                )}
              </div>
              <div style={{ 
                padding: '12px 20px', 
                borderTop: '1px solid #dee2e6', 
                display: 'flex', 
                gap: 8, 
                justifyContent: 'flex-end' 
              }}>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={closeRefundModal} 
                  disabled={submitting}
                >
                  {L.common.cancel}
                </button>
                {refundReasonType === 'other' && (
                  <button 
                    type="submit" 
                    className="btn bg-instagram text-white" 
                    disabled={submitting || !refundReason.trim()}
                  >
                    {submitting ? L.refundPage.submitting : L.refundPage.submitRequest}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
      
      <Footer />
    </>
  );
};

export default Refund;
